<script lang="ts">
import type { CSSProperties } from 'vue'
// SourceHTMLAttributes
import type { __ImageFragment, __ImageTaggedRatio } from '../generated/graphql'
export type ImageValue = Replace<__ImageFragment, Partial2<{ alt: Partial2<{ value: string; translations: Partial2<{ value: string; lang: string }>[] }> }>>
export type ImageRatio = 'none' | 'square' | '16:9' | '5:2' | '2:3' | '4:5'

export const sizesConst = [
  // 12,
  50,
  // 80,
  120,
  //
  220,
  // 320,
  // 390,
  420,
  // 640,
  860,
  // 1080,
  1280,
  // 1440,
  1680,
  // 1920,
  // 2100,
  2560,
] as const

export type ImageSize = (typeof sizesConst)[number]

type ImageSizeVW = 25 | 33 | 50 | 75 | 100
// type ImageExtention = 'webp' | 'jpeg' | 'avif'
// const imageTypes: ImageExtention[] = ['webp', 'jpeg']

export type Props = {
  video?: boolean
  height?: string | number
  autoplay?: boolean | 'never'
  width?: string | number
  sizes: `${ImageSizeVW}vw` | `${ImageSize}px` | None | `fixed:${ImageSize}`
  lazy?: boolean | None
  noColor?: boolean | None
  src?: string | None
  alt?: string | None
  alt2?: string | None
  img?: ImageValue | ImageValue['image2'] | None
  ratio?: ImageRatio | __ImageTaggedRatio | None
  fit?: 'streach' | 'fit' | 'center'
  classImg?: string | None
  onLoad?: (e: Event) => unknown
}
// # a = 16:9
// # b = 5:2
// # s = 1:1
// # c = 2:3
// # d = 4:5
export type AspectValues = {
  letter: 'a' | 'b' | 's' | 'w' | 'c' | 'd'
  aspect: 'natural' | 'square' | '5:2' | '16:9' | '2:3' | '4:5'
  xy: { x: number; y: number } | undefined
  className: string | undefined
}

export const aspectMaps = {
  '16:9': {
    aspect: '16:9',
    letter: 'a',
    xy: { x: 16, y: 9 },
    className: '_a16',
  },
  '2:3': {
    aspect: '2:3',
    letter: 'c',
    xy: { x: 2, y: 3 },
    className: '_a23',
  },
  '4:5': {
    aspect: '4:5',
    letter: 'd',
    xy: { x: 4, y: 5 },
    className: '_a45',
  },
  '5:2': {
    aspect: '5:2',
    letter: 'b',
    xy: { x: 5, y: 2 },
    className: '_a5',
  },
  none: {
    aspect: 'natural',
    letter: 'w',
    xy: undefined,
    className: undefined,
  },
  square: {
    aspect: 'square',
    letter: 's',
    xy: undefined,
    className: '_a1',
  },
} satisfies Record<ImageRatio, AspectValues>

export const ratioMap: Record<__ImageTaggedRatio, ImageRatio> = {
  None: 'none',
  R16x9: '16:9',
  R5x2: '5:2',
  Square: 'square',
  R2x3: '2:3',
  R4x5: '4:5',
  Cover: 'none',
}

export type VideoValue = {
  src: string | undefined
  hls: string | undefined
  type: string
  audio: boolean
}
</script>

<script setup lang="ts">
type UiValuesPicture = {
  isErrorLoading?: boolean
  url: string | None
  fallbackUrl: string
  width: string | number | None
  height: string | number | None
  aspectValue: None | { x: number; y: number }
}

const props = defineProps<Props>()
const runtimeConfig = useRuntimeConfig()

function getRatio(r: Props['ratio']) {
  if (!r) return aspectMaps.none
  const ratio = r in ratioMap ? ratioMap[r as __ImageTaggedRatio] : (r as ImageRatio)
  if (!ratio) return aspectMaps.none
  return aspectMaps[ratio] || aspectMaps.none
}

function toAspect(picture: None | UiValuesPicture, aspect: AspectValues): { width: number | string; height: number | string } | undefined {
  if (!picture) return undefined
  const { height, width, aspectValue } = picture
  const returnNatural = () => (height && width ? { width, height } : undefined)

  if (aspect.aspect === 'natural') return returnNatural()

  const w = (width && +width) || undefined

  if (aspect.aspect === 'square') {
    const val = w || (height && +height)
    return val ? { width: val, height: val } : undefined
  }

  const target = aspect.xy
  if (!target) return
  const res = aspectValue?.x === target.x && aspectValue.y === target.y && returnNatural()
  if (res) return res
  return w ? { width: w, height: Math.ceil((w * target.y) / target.x) } : undefined
}

const mapDpr: Record<ImageSize, ImageSize[]> = {
  50: [50, 120, 220, 420],
  120: [120, 220, 420, 860],
  220: [220, 420, 860],
  420: [420, 860, 1280, 1680],
  860: [860, 1680, 2560],
  1280: [1280, 2560],
  1680: [1680, 2560],
  2560: [2560],
}

function toCorrectSize(numb: number) {
  return (sizesConst.find((size) => size >= numb) || sizesConst.at(-1)) as number
}

const _parseSizesStart = 'fixed:'
function parseFixed(sizes: NN<Props['sizes']>): number | undefined {
  const i = Number.parseInt(sizes.slice(_parseSizesStart.length), 10)
  return Number.isNaN(i) ? undefined : toCorrectSize(i)
}

function toSizesArr(sizes: Props['sizes'], width: number | None): number[] {
  if (sizes?.startsWith(_parseSizesStart)) {
    const fixed = parseFixed(sizes)
    return fixed ? [fixed] : []
  }

  if (sizes?.endsWith('px')) {
    let numb = Number.parseInt(sizes, 10)

    if (!Number.isNaN(numb)) {
      numb = toCorrectSize(numb)

      if (width && width < numb) {
        const sizesPx = sizesConst.find((size) => size >= width)
        if (sizesPx) return [sizesPx]
      }

      const others = numb && numb in mapDpr ? mapDpr[numb as ImageSize] : undefined
      if (!width && others?.length) return others

      if (width && others?.length) {
        const index = others.findIndex((i) => i >= width)
        const res = index === -1 ? others : others.slice(0, index + 1)
        if (res.length) return res
      }
    }
  }

  const index = !width ? -1 : sizesConst.findIndex((i) => i >= width)
  if (index === -1) return sizesConst as unknown as number[]
  return sizesConst.slice(0, index + 1)
}

// function toSource(aspect: AspectValues, picture: UiValuesPicture | None, sizes: Props['sizes']): SourceHTMLAttributes[] {
//   if (!picture || !picture.url) return []
//   const { url, width } = picture
//   const baseUrl = runtimeConfig.public?.imgProxyUrl
//   if (!baseUrl || typeof baseUrl !== 'string') return []
//   const fixed = sizes?.startsWith(_parseSizesStart) ? parseFixed(sizes) : undefined
//   const sizes1: string | undefined = fixed ? `${fixed}px` : sizes || undefined
//   const sizesArr = toSizesArr(sizes, !width ? undefined : typeof width === 'string' ? Number.parseInt(width, 10) : width)
//   return imageTypes.map((ext) => ({
//     type: `image/${ext}`,
//     srcset: helpers.joinStringCb(', ', sizesArr, (w) => `${baseUrl}/${aspect.letter}${w}:${ext}/plain/${url} ${w}w`),
//     sizes: sizes1,
//   }))
// }

function toVideo(img: Props['img'], image2: __ImageFragment['image2']): VideoValue | undefined {
  let isVideo = false
  if (props.video && typeof props.src === 'string') {
    return { src: props.src, type: 'video/mp4', hls: undefined, audio: !!image2?.audio } satisfies VideoValue
  }

  if (helpers.isObject(img) && 'isVideo' in img && img.isVideo) isVideo = true
  if (image2?.videoHls || image2?.isVideo || image2?.video) isVideo = true
  if (!isVideo) return

  const video = !img ? null : image2?.video || ('original' in img ? img.original : undefined)
  if (!image2?.videoHls && typeof video !== 'string') return

  return {
    src: typeof video === 'string' ? video : undefined,
    type: image2?.type || 'video/mp4',
    hls: image2?.videoHls || undefined,
    audio: !!image2?.audio,
  } satisfies VideoValue
}

const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

function buildSrcset(aspect: AspectValues, picture: UiValuesPicture | None, sizes: Props['sizes']): { srcset: string | undefined; one: string | undefined } | undefined {
  if (!picture || !picture.url) return undefined
  const { url, width } = picture
  const baseUrl = runtimeConfig.public?.imgProxyUrl
  if (!baseUrl || typeof baseUrl !== 'string') return undefined
  const sizesArr = toSizesArr(sizes, !width ? undefined : typeof width === 'string' ? Number.parseInt(width, 10) : width)
  const toUrl = (w: number) => `${baseUrl}/${aspect.letter}${w}/plain/${url}`
  const toSrc = (w: number) => `${toUrl(w)} ${w}w`
  const one = sizesArr.length === 1 ? sizesArr[0] : undefined
  return {
    one: one ? toUrl(one) : undefined,
    srcset: one ? undefined : helpers.joinStringCb(', ', sizesArr, toSrc),
  }
}

const ui = computed(() => {
  const { img, ratio, noColor, lazy, src, fit, sizes, onLoad, width, height } = props
  const image2 = !helpers.isObject(img) ? undefined : 'image2' in img ? img.image2 : 'fallbackUrl' in img || 'videoHls' in img ? img : undefined

  const render = !img ? null : 'url' in img ? img : image2
  const aspect = getRatio(ratio)
  const fitClass = fit === 'fit' ? 'fit' : fit === 'streach' ? 'streach' : fit === 'center' ? 'fitCenter' : undefined
  const background = (!noColor && image2?.hsla && toColorHsla(image2?.hsla, 0.3)) || undefined
  const aspectRatio = aspect.aspect === 'natural' && image2?.aspect && fit !== 'fit' ? `${image2.aspect.x}/${image2.aspect.y}` : undefined
  const className = fitClass || aspect?.className
  const picture = render
    ? !render.url
      ? undefined
      : { aspectValue: render.aspect, url: render.url, fallbackUrl: render.fallbackUrl || render.url, width: render.width, height: render.height }
    : src
      ? { url: null, fallbackUrl: src, width: null, height: null, aspectValue: null }
      : null
  const alt = (props.alt ? props.alt : helpers.toString(img && 'image2' in img ? img?.alt?.value : img?.alt)) || props.alt2 || ''
  const loading = lazy ? ('lazy' as const) : ('eager' as const)
  const style: CSSProperties = {}
  if (background) style.background = background
  if (aspectRatio) style.aspectRatio = aspectRatio
  const classes = []
  if (className) classes.push(className)
  if (!picture) classes.push('no')
  const simple: boolean = !sizes || !picture?.url
  const fixed = sizes?.startsWith(_parseSizesStart) ? parseFixed(sizes) : undefined
  const sizesAttr: string | undefined = fixed ? `${fixed}px` : sizes || undefined
  const srcset = buildSrcset(aspect, picture, sizes)
  const noProxy = image2?.noProxy
  return {
    simple,
    video: image2 ? toVideo(img, image2) : undefined,
    classes,
    picture,
    style,
    bind: {
      ...(toAspect(picture, aspect) || (width || height ? { width, height } : undefined)),
      src: noProxy ? picture?.fallbackUrl : srcset?.one || (!srcset?.one && !srcset?.srcset?.length ? picture?.fallbackUrl : TRANSPARENT_PIXEL),
      srcset: noProxy || simple ? undefined : srcset?.srcset,
      sizes: sizesAttr,
      alt,
      loading,
      ...(onLoad && { onLoadPassive: onLoad }),
      ...(!lazy && { fetchpriority: 'high' as const }),
      class: ['_img2', props.classImg],
    },
  }
})

const videoElem = useTemplateRef('video')

// const render = computed(() => img.value.loading === 'eager')

// <!-- https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img -->
// <!-- :decoding="lazy ? 'async' : 'sync'"
// :fetchpriority="lazy ? 'auto' : 'high'" -->

// <!-- <picture v-if="!sizes || !ui.picture.url">
//   <img v-bind="ui.bind" decoding="async" />
// </picture>

// <picture v-else>
//   <source v-for="item in ui.sources" :key="item.type" v-bind="item" />
//   <img v-bind="ui.bind" decoding="async" />
// </picture> -->
</script>

<template>
  <span class="_img" :class="ui.classes" :style="ui.style">
    <template v-if="ui.picture">
      <img v-bind="ui.bind" decoding="async" />
    </template>
    <template v-if="ui.video">
      <video ref="video" class="_video" preload="none" loop muted playsinline webkit-playsinline x5-playsinline :src="ui.video.src" />
      <LazyBaseImageVideo v-if="videoElem" :video="ui.video" :elem="() => videoElem" :autoplay />
    </template>
    <slot />
  </span>
</template>
